import type { IssuedCredentials } from './issued-credentials'
import { useMutation } from '@apollo/client/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import ConfirmDialog from '~/components/ConfirmDialog'
import { GQL_RESET_USERNAME_LEARNER_PASSWORD } from '~/gql/users/username-learners'
import { userFullName } from '~/helpers/users/display-name'

export type ResetTarget = { id: string, username: string, firstName?: string | null, lastName?: string | null }

type IProps = {
  learner: ResetTarget
  onCancel: () => void
  /** Le nouveau mot de passe, à remettre : l'ancien ne connecte plus. */
  onIssued: (credentials: IssuedCredentials) => void
}

/**
 * Nouveau mot de passe pour un apprenant en pseudo (#271), qui n'a pas d'e-mail
 * pour un « mot de passe oublié ». Confirmé d'abord : l'ancien cesse de marcher
 * et ses sessions ouvertes tombent.
 */
function ResetUsernamePasswordDialog({ learner, onCancel, onIssued }: IProps) {
  const { t } = useTranslation()
  // `loading` bloque le second clic : deux réinitialisations croisées afficheraient un mot de passe déjà remplacé.
  const [reset, { loading }] = useMutation(GQL_RESET_USERNAME_LEARNER_PASSWORD)

  const confirm = async () => {
    try {
      const { data } = await reset({ variables: { userId: learner.id } })
      if (!data) return
      const { username, password } = data.resetUsernameLearnerPassword
      const name = userFullName(learner)
      onIssued({ username, password, ...(name && { name }) })
    } catch {
      toast.error(t('credentials.reset.failed'))
      onCancel()
    }
  }

  return (
    <ConfirmDialog open title={t('credentials.reset.title', { username: learner.username })} confirmLabel={t('credentials.reset.confirm')} pending={loading} onConfirm={confirm} onCancel={onCancel}>
      {t('credentials.reset.body')}
    </ConfirmDialog>
  )
}

export default ResetUsernamePasswordDialog
